import { hasBulkVersion, InboxSelectionCheckbox, useInboxBulkActions } from "./inbox-bulk-actions";
import { OverviewHeadTools, type OverviewHeadList } from "./overview-head-tools";
import { incomingWorkRead, type IncomingWorkRead } from "./overview-incoming-work";
// The start page (#2003): the summary cards filter one server-projected worklist, with the
// Performance and Team panels beside it. Until then the cards and a short queue were the Overview
// page and this list was a page of its own, the Inbox.
import React from "react";
import { Banner, Button, DataTable, Input, Select, StatusPill, type StatusTone } from "@/ds";
import { cockpit, prefetchRequest } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type {
  AppConfigInfo,
  ConversationAttentionReason,
  ConversationListItem,
  ConversationListOptions,
  ConversationListPage,
  ConversationSort,
} from "@/api";
import { useLiveInvalidation, useLiveScopeKey } from "@/live/live-provider";
import { cx, fmtDate, fmtStamp, Icon } from "@/lib";
import { roleAtLeast } from "@/lib/roles";
import type { PushToast } from "@/notifications/toast-queue";
import { pathOf, viewFromPath } from "@/router";
import { useSession } from "@/session";
import {
  readPrimaryScrollTop,
  withoutTrashedRequest,
  subscribePrimaryScroll,
  writePrimaryScrollTop,
  type InboxReturnContext,
} from "@/worklist-return-context";
import { useT } from "../i18n";
import { DeliveryReconciliationQueue } from "./delivery-reconciliation";
import {
  assigneeName,
  assigneeSummary,
  conversationDateQuery,
  conversationQueryForTask,
  EMPTY_INBOX_DATE_RANGE,
  eventSummaryParts,
  inboxDateRangeProblem,
  inboxPeriodDateRange,
  groupedPreviewRemainingCount,
  inboxTaskCounts,
  isGroupedInboxView,
  INBOX_ACTIVE_SECTIONS,
  INBOX_DEFAULT_SORT,
  INBOX_PERIOD_PRESETS,
  INBOX_SORTS,
  INBOX_STATUS_BUCKETS,
  acceptServerConversationPage,
  type InboxActiveSection,
  type InboxAssignmentFilter,
  type InboxDateRange,
  type InboxPeriodPreset,
  type InboxStatusBucket,
  type InboxTaskCounts,
  type InboxTaskFilter,
} from "./screen-inbox.logic";
import { relativeAge } from "./screen-overview.logic";
import { conversationCustomerLabel, FormCustomerSource } from "./conversation-customer";
import { guestCountText } from "./guest-count-value";
import { OverviewMetricsPanel } from "./overview-metrics";
import { PresencePanel } from "./presence-panel";
import { RowAssignees } from "./row-assignees";

const CONVERSATION_PAGE_SIZE = 50;
const GROUPED_PREVIEW_PAGE_SIZE = 3;
const EMPTY_TASK_COUNTS: InboxTaskCounts = { new: 0, review: 0, waiting: 0, all: 0, archived: 0, trash: 0 };
const EMPTY_SECTION_PAGES: Record<InboxActiveSection, ConversationListPage | null> = {
  new: null,
  review: null,
  waiting: null,
};
const EMPTY_SECTION_OFFSETS: Record<InboxActiveSection, number> = { new: 0, review: 0, waiting: 0 };
/** The list a visit starts on. Coming back from a request restores the one that was in use. */
const DEFAULT_TASK: InboxTaskFilter = "new";
const STAT_ICONS: Record<InboxActiveSection, string> = { new: "mail", review: "clock", waiting: "reply" };
const ATTENTION_REASON_TONES: Record<ConversationAttentionReason, StatusTone> = {
  new_reply: "info",
  human_review: "warning",
  decision_recompute: "danger",
  stale_draft: "danger",
  draft_rejected: "danger",
  draft_review: "warning",
  draft_send: "warning",
  new_request: "info",
};

function finalPageOffset(total: number, limit: number): number {
  return total === 0
    ? 0
    : Math.floor((total - 1) / limit) * limit;
}

interface InboxReadInput {
  taskFilter: InboxTaskFilter;
  assignment: InboxAssignmentFilter;
  statusFilter: InboxStatusBucket | "";
  q: string;
  dateRange: InboxDateRange;
  periodPreset: InboxPeriodPreset;
  sort: ConversationSort;
  offset: number;
  sectionOffsets: Record<InboxActiveSection, number>;
}

/** One worklist page of a view; `options` is null for a task this assignment can never list. */
interface InboxPageRead {
  task: InboxTaskFilter;
  offset: number;
  limit: number;
  options: ConversationListOptions | null;
}

interface InboxReads {
  pages: InboxPageRead[];
  summary: ConversationListOptions;
  archivedSummary: ConversationListOptions;
  trashSummary: ConversationListOptions;
}

/** Everything one Inbox view asks the server for. Loading and the #1536 cache use the same reads. */
function inboxReads(input: InboxReadInput, now: Date): InboxReads {
  // Named periods follow the tenant calendar whenever a focus, visibility or live refresh
  // crosses a day, week or month boundary. Custom input remains exactly as entered.
  const currentDateRange = input.periodPreset === "custom"
    ? input.dateRange
    : inboxPeriodDateRange(input.periodPreset, now, input.dateRange);
  const dateQuery = conversationDateQuery(currentDateRange);
  const page = (task: InboxTaskFilter, taskOffset: number, limit: number): InboxPageRead => {
    const taskQuery = conversationQueryForTask(task, input.assignment, input.statusFilter);
    return {
      task,
      offset: taskOffset,
      limit,
      options: taskQuery
        ? { group_by: "request", q: input.q, ...dateQuery, sort: input.sort, ...taskQuery, limit, offset: taskOffset }
        : null,
    };
  };
  return {
    pages: isGroupedInboxView(input.taskFilter, input.statusFilter)
      ? INBOX_ACTIVE_SECTIONS.map((section) => page(section, input.sectionOffsets[section], GROUPED_PREVIEW_PAGE_SIZE))
      : [page(input.taskFilter, input.offset, CONVERSATION_PAGE_SIZE)],
    // Each scope is counted by the server that lists it, so a badge can never disagree with the
    // page it opens. Both summaries are limit=1 — they ask for a number, not a worklist.
    summary: { group_by: "request", q: input.q, ...dateQuery, limit: 1, offset: 0 },
    archivedSummary: { group_by: "request", q: input.q, ...dateQuery, scope: "archived", limit: 1, offset: 0 },
    trashSummary: { group_by: "request", q: input.q, ...dateQuery, scope: "trash", limit: 1, offset: 0 },
  };
}

function emptyInboxPage(read: InboxPageRead, sort: ConversationSort): ConversationListPage {
  const scope = read.task === "archived" || read.task === "trash" ? read.task : "active";
  return emptyConversationPage(scope, sort, read.offset, read.limit);
}

function readInboxPage(read: InboxPageRead, sort: ConversationSort): Promise<ConversationListPage> {
  return read.options ? cockpit.listConversations(read.options) : Promise.resolve(emptyInboxPage(read, sort));
}

interface InboxView {
  pages: ConversationListPage[];
  summary: ConversationListPage | undefined;
  archivedSummary: ConversationListPage | undefined;
  trashSummary: ConversationListPage | undefined;
}

/** The request a press on a worklist row opens: its own row link, unless the press is on another
 * control of the row (a checkbox, an assignee, a pill button), which opens nothing. */
function prefetchPressedRequest(target: HTMLElement): void {
  const control = target.closest("a,button,input,select,textarea,label,[role='button']");
  const link = control ? control.closest<HTMLAnchorElement>("a.work-item-link")
    : target.closest("tr")?.querySelector<HTMLAnchorElement>("a.work-item-link");
  const href = link?.getAttribute("href");
  const view = href ? viewFromPath(href) : null;
  if (view?.name === "mail") prefetchRequest(cockpit, view.id);
}

/**
 * Remembered worklist pages do not depend on the summary cache. A page past its
 * last offset counts as missing: the live load moves the offset back rather than showing it.
 */
function cachedInboxView(reads: InboxReads, sort: ConversationSort): InboxView | null {
  const pages: ConversationListPage[] = [];
  for (const read of reads.pages) {
    const page = read.options ? cachedRead("listConversations", read.options) : emptyInboxPage(read, sort);
    if (!page || read.offset > finalPageOffset(page.total, page.limit)) return null;
    pages.push(page);
  }
  const summary = cachedRead("listConversations", reads.summary);
  const archivedSummary = cachedRead("listConversations", reads.archivedSummary);
  const trashSummary = cachedRead("listConversations", reads.trashSummary);
  return { pages, summary, archivedSummary, trashSummary };
}

function sectionPagesOf(pages: ConversationListPage[]): Record<InboxActiveSection, ConversationListPage> {
  return Object.fromEntries(
    INBOX_ACTIVE_SECTIONS.map((section, index) => [section, pages[index]!]),
  ) as Record<InboxActiveSection, ConversationListPage>;
}

function initialFiltersExpanded(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(min-width: 768px)").matches;
}

function emptyConversationPage(
  scope: ConversationListPage["scope"],
  sort: ConversationSort,
  offset: number,
  limit: number,
): ConversationListPage {
  return {
    items: [],
    total: 0,
    scope,
    sort,
    limit,
    offset,
    next_offset: null,
    counts: {
      total: 0,
      attention: 0,
      unassigned: 0,
      by_status: { new: 0, in_progress: 0, new_message: 0, answered: 0, done: 0 },
    },
  };
}

function AgeCell({ value }: { value: string | null | undefined }) {
  const t = useT();
  const age = relativeAge(value, new Date());
  return (
    <span className="inbox-activity">
      <span className="inbox-activity__date">{value ? fmtStamp(value) : "—"}</span>
      <span className="inbox-age neo-mono">{age ? t(age.key, { n: age.n }) : "—"}</span>
    </span>
  );
}

export interface InboxScreenProps {
  onOpen?: (id: string) => void;
  pushToast?: PushToast;
  config?: AppConfigInfo | null;
  initialContext?: InboxReturnContext;
  onContextChange?: (context: InboxReturnContext) => void;
  /** #2104: the way to Filed, a page of its own. App owns the route; the count is its open mail. */
  filed?: { href: string; count: number; onOpen: () => void };
  /** #2259: the list another page's head switch asked for, when there is no context to restore. */
  initialList?: OverviewHeadList;
  onOpenStaff?: () => void;
  onOpenSystemStatus?: () => void;
}

function inboxFeatureState(value: boolean | undefined): "on" | "off" | "unknown" {
  return value === true ? "on" : value === false ? "off" : "unknown";
}

function InboxFooterFacts({
  config,
  terminalAnalysisFailures,
}: {
  config: AppConfigInfo | null | undefined;
  terminalAnalysisFailures: number | null;
}) {
  const t = useT();
  const features = [
    {
      key: "ingest",
      label: t("inbox.footer.ingest"),
      state: inboxFeatureState(config?.features?.ingest),
    },
    {
      key: "ai-extraction",
      label: t("inbox.footer.aiExtraction"),
      state: inboxFeatureState(config?.features?.ai_extraction),
    },
  ];
  const rulesetVersion = config?.ruleset_version?.trim() || null;

  return <>
    {features.map((feature) => (
      <span
        key={feature.key}
        className={`inbox-status-fact inbox-status-fact--${feature.state}`}
        data-testid={`inbox-footer-${feature.key}`}
      >
        <span className="inbox-status-fact__dot" aria-hidden="true" />
        <span>{feature.label}: {t(`inbox.footer.state.${feature.state}`)}</span>
      </span>
    ))}
    <span
      className={`inbox-status-fact${rulesetVersion ? "" : " inbox-status-fact--unknown"}`}
      data-testid="inbox-footer-ruleset"
    >
      <span className="inbox-status-fact__dot" aria-hidden="true" />
      <span>
        {t("inbox.footer.ruleset")}: <span className="neo-mono">
          {rulesetVersion ?? t("inbox.footer.state.unknown")}
        </span>
      </span>
    </span>
    {terminalAnalysisFailures !== null && terminalAnalysisFailures > 0 && (
      <span
        className="inbox-status-fact inbox-status-fact--warning"
        data-testid="inbox-footer-analysis-warning"
      >
        <span className="inbox-status-fact__dot" aria-hidden="true" />
        <span>{t(
          terminalAnalysisFailures === 1
            ? "health.failedRuns.one"
            : "health.failedRuns.other",
          { count: terminalAnalysisFailures },
        )}</span>
      </span>
    )}
  </>;
}

function InboxPeriodControl({
  preset,
  range,
  problem,
  onPreset,
  onRange,
}: {
  preset: InboxPeriodPreset;
  range: InboxDateRange;
  problem: ReturnType<typeof inboxDateRangeProblem>;
  onPreset: (preset: InboxPeriodPreset) => void;
  onRange: (change: Partial<InboxDateRange>) => void;
}) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const firstPresetRef = React.useRef<HTMLButtonElement>(null);
  const dialogId = React.useId();
  const titleId = `${dialogId}-title`;

  React.useEffect(() => {
    if (!open) return;
    firstPresetRef.current?.focus();
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const closeAndRestoreFocus = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const choosePreset = (next: InboxPeriodPreset) => {
    onPreset(next);
    if (next !== "custom") closeAndRestoreFocus();
  };

  return (
    <div className="inbox-period" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="inbox-period__trigger neo-btn neo-btn--secondary neo-btn--sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="calendar" size={15} />
        <span>{t(`inbox.period.${preset}`)}</span>
        <Icon name="chevronDown" size={13} />
      </button>

      {open && (
        <div
          id={dialogId}
          className="inbox-period__popover"
          role="dialog"
          aria-labelledby={titleId}
        >
          <div className="inbox-period__title" id={titleId}>{t("inbox.period.title")}</div>
          <div className="inbox-period__presets" role="group" aria-label={t("inbox.period.presetsAria")}>
            {INBOX_PERIOD_PRESETS.map((name, index) => (
              <button
                ref={index === 0 ? firstPresetRef : undefined}
                key={name}
                type="button"
                aria-pressed={preset === name}
                onClick={() => choosePreset(name)}
              >
                {t(`inbox.period.${name}`)}
              </button>
            ))}
          </div>
          <div className="inbox-period__custom" data-testid="inbox-period-custom">
            <label>
              <span>{t("inbox.filterDateFromAria")}</span>
              <Input
                size="sm"
                type="date"
                value={range.from}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onRange({ from: event.target.value })}
                aria-label={t("inbox.filterDateFromAria")}
                aria-invalid={problem ? true : undefined}
              />
            </label>
            <label>
              <span>{t("inbox.filterDateToAria")}</span>
              <Input
                size="sm"
                type="date"
                value={range.to}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onRange({ to: event.target.value })}
                aria-label={t("inbox.filterDateToAria")}
                aria-invalid={problem ? true : undefined}
              />
            </label>
          </div>
          <div className="inbox-period__footer">
            <Button variant="ghost" size="sm" onClick={closeAndRestoreFocus}>
              {t("common.close")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function InboxScreen({
  onOpen,
  pushToast,
  config,
  initialContext,
  onContextChange,
  filed,
  initialList,
  onOpenStaff,
  onOpenSystemStatus,
}: InboxScreenProps) {
  const t = useT();
  const { user } = useSession();
  const scopeKey = useLiveScopeKey();
  const currentScope = React.useRef(scopeKey);
  currentScope.current = scopeKey;
  const [incomingRead, setIncomingRead] = React.useState<{ scope: string; read: IncomingWorkRead }>(
    { scope: scopeKey, read: { status: "loading" } },
  );
  const canAssign = roleAtLeast(user?.role ?? "", "admin") && Boolean(user?.tenantId);
  const initialTask = initialContext?.taskFilter ?? initialList ?? DEFAULT_TASK;
  const initialAssignment = initialContext?.assignment ?? "all";
  const initiallyGrouped = isGroupedInboxView(initialTask, initialContext?.statusFilter ?? "");
  // Return rows survive mutation cache invalidation; refresh them without hiding the list.
  const [initialView] = React.useState(() => {
    const initialDateRange = initialContext ? initialContext.dateRange : EMPTY_INBOX_DATE_RANGE;
    if (initialContext || inboxDateRangeProblem(initialDateRange)) return null;
    const initialSort = initialContext?.sort ?? INBOX_DEFAULT_SORT;
    return cachedInboxView(inboxReads({
      taskFilter: initialTask,
      assignment: initialAssignment,
      statusFilter: initialContext?.statusFilter ?? "",
      q: initialContext?.query ?? "",
      dateRange: initialDateRange,
      periodPreset: initialContext?.periodPreset ?? "all",
      sort: initialSort,
      offset: initialContext?.offset ?? 0,
      sectionOffsets: initialContext ? initialContext.sectionOffsets : EMPTY_SECTION_OFFSETS,
    }, new Date()), initialSort);
  });
  const [conversationPage, setConversationPage] = React.useState<ConversationListPage | null>(
    initialView
      ? (initiallyGrouped ? null : initialView.pages[0]!)
      : initialContext?.conversationPage ?? null,
  );
  const [sectionPages, setSectionPages] = React.useState<Record<InboxActiveSection, ConversationListPage | null>>(() => (
    initialView && initiallyGrouped
      ? sectionPagesOf(initialView.pages)
      : initialContext ? { ...initialContext.sectionPages } : EMPTY_SECTION_PAGES
  ));
  const [sectionOffsets, setSectionOffsets] = React.useState(() => (
    initialContext ? { ...initialContext.sectionOffsets } : EMPTY_SECTION_OFFSETS
  ));
  const [taskCounts, setTaskCounts] = React.useState<InboxTaskCounts>(
    initialView?.summary && initialView.archivedSummary && initialView.trashSummary
      ? inboxTaskCounts(initialView.summary, initialView.archivedSummary, initialView.trashSummary)
      : initialContext?.taskCounts ?? EMPTY_TASK_COUNTS,
  );
  // The cards show a dash rather than a zero until the server has counted once.
  const [countsKnown, setCountsKnown] = React.useState(Boolean(
    (initialContext && initialContext.countsKnown !== false) || (initialView?.summary && initialView.archivedSummary && initialView.trashSummary),
  ));
  const hasInitialPages = Boolean(initialView || (initiallyGrouped
    ? initialContext && INBOX_ACTIVE_SECTIONS.every((section) => initialContext.sectionPages[section])
    : initialContext?.conversationPage));
  const [loading, setLoading] = React.useState(!hasInitialPages);
  const [error, setError] = React.useState<string | null>(null);
  const [q, setQ] = React.useState(initialContext?.query ?? "");
  const [taskFilter, setTaskFilter] = React.useState<InboxTaskFilter>(initialTask);
  const [statusFilter, setStatusFilter] = React.useState<InboxStatusBucket | "">(
    initialContext?.statusFilter ?? "",
  );
  const groupedView = isGroupedInboxView(taskFilter, statusFilter);
  const [assignment, setAssignment] = React.useState<InboxAssignmentFilter>(initialAssignment);
  const [dateRange, setDateRange] = React.useState<InboxDateRange>(() => (
    initialContext ? { ...initialContext.dateRange } : EMPTY_INBOX_DATE_RANGE
  ));
  const [periodPreset, setPeriodPreset] = React.useState<InboxPeriodPreset>(initialContext?.periodPreset ?? "all");
  const [sort, setSort] = React.useState<ConversationSort>(initialContext?.sort ?? INBOX_DEFAULT_SORT);
  const [offset, setOffset] = React.useState(initialContext?.offset ?? 0);
  const [filtersExpanded, setFiltersExpanded] = React.useState(initialFiltersExpanded);
  const [collapsedSections, setCollapsedSections] = React.useState<Record<InboxActiveSection, boolean>>({
    new: false,
    review: false,
    waiting: false,
  });
  const [terminalAnalysisFailures, setTerminalAnalysisFailures] = React.useState<number | null>(null);
  const statRefs = React.useRef<Partial<Record<InboxActiveSection, HTMLButtonElement>>>({});
  // The page's last element; the delivery check renders the status footer into it.
  const [footerHost, setFooterHost] = React.useState<HTMLElement | null>(null);
  // A press that starts in the assignee picker belongs to the picker, also when the row moves under
  // the pointer before release and the click lands on the row itself.
  const pressInAssigneeRef = React.useRef(false);
  const viewKey = JSON.stringify([taskFilter, assignment, statusFilter, q, dateRange.from,
    dateRange.to, periodPreset, sort]);
  const readKey = JSON.stringify([viewKey, offset, sectionOffsets]);
  const [loadedReadKey, setLoadedReadKey] = React.useState(hasInitialPages ? readKey : null);
  const availableReadKey = React.useRef(loadedReadKey);
  const loadSequence = React.useRef(0);
  const healthAlertRequestSequence = React.useRef(0);
  const contextRef = React.useRef<InboxReturnContext>({
    kind: "inbox",
    conversationPage: initialContext?.conversationPage ?? null,
    sectionPages: initialContext ? { ...initialContext.sectionPages } : EMPTY_SECTION_PAGES,
    taskCounts: initialContext?.taskCounts ?? EMPTY_TASK_COUNTS,
    query: initialContext?.query ?? "",
    taskFilter: initialTask,
    statusFilter: initialContext?.statusFilter ?? "",
    assignment: initialAssignment,
    dateRange: initialContext ? { ...initialContext.dateRange } : EMPTY_INBOX_DATE_RANGE,
    periodPreset: initialContext?.periodPreset ?? "all",
    sort: initialContext?.sort ?? INBOX_DEFAULT_SORT,
    offset: initialContext?.offset ?? 0,
    sectionOffsets: initialContext ? { ...initialContext.sectionOffsets } : EMPTY_SECTION_OFFSETS,
    scrollTop: initialContext?.scrollTop ?? 0,
    focusedRow: initialContext?.focusedRow ?? null,
  });
  const onContextChangeRef = React.useRef(onContextChange);
  onContextChangeRef.current = onContextChange;
  const restorationRef = React.useRef(initialContext ?? null);
  const restorationInFlightRef = React.useRef(Boolean(initialContext));

  const publishContext = React.useCallback((patch: Partial<InboxReturnContext> = {}) => {
    contextRef.current = { ...contextRef.current, ...patch };
    onContextChangeRef.current?.(contextRef.current);
  }, []);

  React.useEffect(() => {
    publishContext({
      conversationPage,
      sectionPages: { ...sectionPages },
      taskCounts,
      countsKnown,
      query: q,
      taskFilter,
      statusFilter,
      assignment,
      dateRange: { ...dateRange },
      periodPreset,
      sort,
      offset,
      sectionOffsets: { ...sectionOffsets },
    });
  }, [
    assignment,
    conversationPage,
    dateRange,
    offset,
    periodPreset,
    publishContext,
    q,
    sectionPages,
    sectionOffsets,
    sort,
    statusFilter,
    taskCounts,
    countsKnown,
    taskFilter,
  ]);

  React.useEffect(() => {
    const capture = () => publishContext({ scrollTop: readPrimaryScrollTop() });
    const unsubscribe = subscribePrimaryScroll(capture);
    return unsubscribe;
  }, [publishContext]);

  // #814: a period the server would refuse is never sent. The operator gets the reason next to
  // the control instead of a failed load, and the page they were reading stays on screen.
  const dateRangeProblem = inboxDateRangeProblem(dateRange);

  const load = React.useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background === true;
    const sequence = ++loadSequence.current;
    const isCurrent = () => sequence === loadSequence.current && scopeKey === currentScope.current;
    setIncomingRead({ scope: scopeKey, read: { status: "loading" } });
    // Reuse the summary request: its incoming-work counts are tenant-wide even when the
    // worklist summary is filtered. Never restore these freshness-sensitive counts from cache.
    const readSummary = (options: ConversationListOptions) => {
      const pending = cockpit.listConversations(options);
      void pending.then(
        (page) => {
          if (isCurrent()) setIncomingRead({ scope: scopeKey, read: incomingWorkRead(page.counts?.incoming_work) });
        },
        () => {
          if (isCurrent()) setIncomingRead({ scope: scopeKey, read: { status: "unavailable" } });
        },
      );
      return pending;
    };
    if (dateRangeProblem) {
      setLoading(false);
      // An invalid local period must not stop the independent business-wide indicators.
      void readSummary({ group_by: "request", limit: 1, offset: 0 }).catch(() => undefined);
      return;
    }
    try {
      const reads = inboxReads({
        taskFilter,
        assignment,
        statusFilter,
        q,
        dateRange,
        periodPreset,
        sort,
        offset,
        sectionOffsets,
      }, new Date());
      if (!background) {
        const cached = cachedInboxView(reads, sort);
        if (cached) {
          if (groupedView) {
            setSectionPages(sectionPagesOf(cached.pages));
            setConversationPage(null);
          } else {
            setConversationPage(cached.pages[0]!);
          }
          if (cached.summary && cached.archivedSummary && cached.trashSummary) {
            setTaskCounts(inboxTaskCounts(cached.summary, cached.archivedSummary, cached.trashSummary));
            setCountsKnown(true);
          }
          availableReadKey.current = readKey;
          setLoadedReadKey(readKey);
        }
        const hasPages = availableReadKey.current === readKey;
        if (!hasPages) {
          availableReadKey.current = null;
          setLoadedReadKey(null);
          setConversationPage(null);
          setSectionPages(EMPTY_SECTION_PAGES);
        }
        setLoading(!hasPages);
        setError(null);
      }
      // Counters may be slower than the worklist. Their failure or latency never owns rows.
      void Promise.all([
        readSummary(reads.summary),
        cockpit.listConversations(reads.archivedSummary),
        cockpit.listConversations(reads.trashSummary),
      ]).then(([summary, archivedSummary, trashSummary]) => {
        if (!isCurrent()) return;
        setTaskCounts(inboxTaskCounts(summary, archivedSummary, trashSummary));
        setCountsKnown(true);
      }, () => { if (isCurrent()) setCountsKnown(false); });

      if (groupedView) {
        let pages = await Promise.all(reads.pages.map((read) => readInboxPage(read, sort)));
        if (!isCurrent()) return;

        const nextOffsets = { ...sectionOffsets };
        let clamped = false;
        INBOX_ACTIVE_SECTIONS.forEach((section, index) => {
          const lastOffset = finalPageOffset(pages[index]!.total, pages[index]!.limit);
          if (nextOffsets[section] > lastOffset) {
            nextOffsets[section] = lastOffset;
            clamped = true;
          }
        });
        if (clamped) {
          // Keep the other sections usable while fetching the previous last page.
          pages = await Promise.all(reads.pages.map((read, index) => {
            const nextOffset = nextOffsets[INBOX_ACTIVE_SECTIONS[index]!];
            return nextOffset === read.offset ? pages[index]! : readInboxPage({
              ...read, offset: nextOffset,
              options: read.options ? { ...read.options, offset: nextOffset } : null,
            }, sort);
          }));
          if (!isCurrent()) return;
          setSectionOffsets(nextOffsets);
        }

        setSectionPages((current) => Object.fromEntries(
          INBOX_ACTIVE_SECTIONS.map((section, index) => [
            section,
            background ? acceptServerConversationPage(current[section], pages[index]!) : pages[index]!,
          ]),
        ) as Record<InboxActiveSection, ConversationListPage>);
        setConversationPage(null);
        const nextReadKey = JSON.stringify([viewKey, offset, nextOffsets]);
        availableReadKey.current = nextReadKey;
        setLoadedReadKey(nextReadKey);
        setError(null);
        return;
      }

      let page = await readInboxPage(reads.pages[0]!, sort);
      if (!isCurrent()) return;
      const lastOffset = finalPageOffset(page.total, page.limit);
      if (offset > lastOffset) {
        const read = reads.pages[0]!;
        page = await readInboxPage({ ...read, offset: lastOffset,
          options: read.options ? { ...read.options, offset: lastOffset } : null,
        }, sort);
        if (!isCurrent()) return;
        setOffset(lastOffset);
      }
      setConversationPage((current) => background ? acceptServerConversationPage(current, page) : page);
      const nextReadKey = JSON.stringify([viewKey, Math.min(offset, lastOffset), sectionOffsets]);
      availableReadKey.current = nextReadKey;
      setLoadedReadKey(nextReadKey);
      setError(null);
    } catch (caught: any) {
      if (isCurrent()) {
        setError(caught?.message || t("inbox.loadError"));
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [
    scopeKey,
    assignment,
    viewKey,
    readKey,
    dateRange.from,
    dateRange.to,
    dateRangeProblem,
    offset,
    periodPreset,
    q,
    sectionOffsets.new,
    sectionOffsets.review,
    sectionOffsets.waiting,
    sort,
    statusFilter,
    taskFilter,
    groupedView,
    t,
  ]);

  const selectionDisabled = loading || Boolean(error) || Boolean(dateRangeProblem) || loadedReadKey !== readKey;
  const bulk = useInboxBulkActions({
    rows: groupedView
      ? INBOX_ACTIVE_SECTIONS.flatMap((section) => collapsedSections[section] ? [] : sectionPages[section]?.items ?? [])
      : conversationPage?.items ?? [],
    scope: `${readKey}:${JSON.stringify(collapsedSections)}`,
    trash: taskFilter === "trash",
    disabled: selectionDisabled,
    pushToast,
    reload: () => load({ background: true }),
    onTrashed: (requestId) => {
      // Drop only a server-confirmed move and prevent older in-flight reads restoring its row.
      loadSequence.current += 1;
      setConversationPage((page) => withoutTrashedRequest(page, requestId));
      setSectionPages((pages) => Object.fromEntries(INBOX_ACTIVE_SECTIONS.map((section) =>
        [section, withoutTrashedRequest(pages[section], requestId)],
      )) as typeof pages);
      setCountsKnown(false);
    },
  });

  React.useEffect(() => {
    void load();
    return () => { loadSequence.current += 1; };
  }, [load]);

  const loadHealthAlerts = React.useCallback(() => {
    const sequence = ++healthAlertRequestSequence.current;
    void cockpit.getTenantHealthAlerts().then(
      (alerts) => {
        if (sequence !== healthAlertRequestSequence.current) return;
        const count = alerts?.retryable_failed_count;
        setTerminalAnalysisFailures(
          typeof count === "number" && Number.isFinite(count) && count >= 0
            ? Math.floor(count)
            : null,
        );
      },
      () => {
        if (sequence === healthAlertRequestSequence.current) setTerminalAnalysisFailures(null);
      },
    );
  }, []);

  React.useEffect(() => {
    loadHealthAlerts();
    return () => { healthAlertRequestSequence.current += 1; };
  }, [loadHealthAlerts]);

  // Also the catch-up after a return to the front: a stream that went quiet meanwhile is reopened,
  // and its opening event moves these domains (#2274).
  const invalidations = useLiveInvalidation("requests", "workspace", "customers", "pipeline");
  React.useEffect(() => {
    if (invalidations === 0) return;
    void load({ background: true });
    loadHealthAlerts();
  }, [invalidations, load, loadHealthAlerts]);

  React.useEffect(() => {
    const saved = restorationRef.current;
    if (!saved || loading || error || typeof window === "undefined" || typeof document === "undefined") return;
    const pagesReady = groupedView
      ? INBOX_ACTIVE_SECTIONS.every((section) => sectionPages[section] !== null)
      : conversationPage !== null;
    if (!pagesReady) return;
    let firstFrame = 0;
    let secondFrame = 0;
    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const savedFocus = saved.focusedRow;
        let focusSection: InboxTaskFilter = groupedView
          ? savedFocus?.section ?? INBOX_ACTIVE_SECTIONS[0]
          : taskFilter;
        const rowsFor = (section: InboxTaskFilter): ConversationListItem[] => {
          if (!groupedView) return conversationPage?.items ?? [];
          return INBOX_ACTIVE_SECTIONS.includes(section as InboxActiveSection)
            ? sectionPages[section as InboxActiveSection]?.items ?? []
            : [];
        };
        let focusRows = rowsFor(focusSection);

        if (savedFocus && focusRows.length === 0 && groupedView) {
          const fallbackSection = INBOX_ACTIVE_SECTIONS.find((section) => rowsFor(section).length > 0);
          if (fallbackSection) {
            focusSection = fallbackSection;
            focusRows = rowsFor(fallbackSection);
          }
        }

        if (savedFocus && focusRows.length > 0) {
          const exactIndex = focusRows.findIndex((row) => row.id === savedFocus.id);
          const focusIndex = exactIndex >= 0
            ? exactIndex
            : Math.min(Math.max(0, savedFocus.index), focusRows.length - 1);
          const section = document.querySelector<HTMLElement>(`[data-testid="inbox-section-${focusSection}"]`);
          const row = section?.querySelectorAll<HTMLElement>("tbody tr[tabindex='0']")[focusIndex];
          row?.focus({ preventScroll: true });
          publishContext({
            focusedRow: {
              id: focusRows[focusIndex]!.id,
              index: focusIndex,
              section: focusSection,
            },
          });
        } else if (savedFocus) {
          document.querySelector<HTMLElement>(".inbox-toolbar input[type='search']")?.focus({ preventScroll: true });
          publishContext({ focusedRow: null });
        }

        writePrimaryScrollTop(saved.scrollTop);
        publishContext({ scrollTop: saved.scrollTop });
        restorationRef.current = null;
        restorationInFlightRef.current = false;
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [conversationPage, error, loading, publishContext, sectionPages, taskFilter, groupedView]);

  React.useEffect(() => {
    if (
      restorationInFlightRef.current
      || loading
      || error
      || typeof window === "undefined"
      || typeof document === "undefined"
    ) return;
    const savedFocus = contextRef.current.focusedRow;
    if (!savedFocus) return;
    if (document.activeElement !== document.body && document.activeElement !== document.documentElement) return;

    let focusSection: InboxTaskFilter = groupedView
      ? savedFocus.section ?? INBOX_ACTIVE_SECTIONS[0]
      : taskFilter;
    const rowsFor = (section: InboxTaskFilter): ConversationListItem[] => {
      if (!groupedView) return conversationPage?.items ?? [];
      return INBOX_ACTIVE_SECTIONS.includes(section as InboxActiveSection)
        ? sectionPages[section as InboxActiveSection]?.items ?? []
        : [];
    };
    let focusRows = rowsFor(focusSection);
    if (focusRows.some((row) => row.id === savedFocus.id)) return;
    if (focusRows.length === 0 && groupedView) {
      const fallbackSection = INBOX_ACTIVE_SECTIONS.find((section) => rowsFor(section).length > 0);
      if (fallbackSection) {
        focusSection = fallbackSection;
        focusRows = rowsFor(fallbackSection);
      }
    }

    const scrollTop = readPrimaryScrollTop();
    const frame = window.requestAnimationFrame(() => {
      if (focusRows.length > 0) {
        const focusIndex = Math.min(Math.max(0, savedFocus.index), focusRows.length - 1);
        const section = document.querySelector<HTMLElement>(`[data-testid="inbox-section-${focusSection}"]`);
        section?.querySelectorAll<HTMLElement>("tbody tr[tabindex='0']")[focusIndex]
          ?.focus({ preventScroll: true });
        publishContext({
          focusedRow: {
            id: focusRows[focusIndex]!.id,
            index: focusIndex,
            section: focusSection,
          },
        });
      } else {
        document.querySelector<HTMLElement>(".inbox-toolbar input[type='search']")?.focus({ preventScroll: true });
        publishContext({ focusedRow: null });
      }
      writePrimaryScrollTop(scrollTop);
      publishContext({ scrollTop });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [conversationPage, error, loading, publishContext, sectionPages, taskFilter, groupedView]);

  const conversations = conversationPage?.items ?? [];
  const groupedTotalCount = INBOX_ACTIVE_SECTIONS.reduce(
    (total, section) => total + (sectionPages[section]?.total ?? 0),
    0,
  );

  const selectTask = (task: InboxTaskFilter) => {
    setTaskFilter(task);
    setAssignment(task === "review" ? "review" : "all");
    setOffset(0);
    setSectionOffsets(EMPTY_SECTION_OFFSETS);
  };
  const openTaskListFromPreview = (task: InboxActiveSection) => {
    setTaskFilter(task);
    setOffset(0);
    setSectionOffsets(EMPTY_SECTION_OFFSETS);
    window.requestAnimationFrame(() => statRefs.current[task]?.focus());
  };
  const updateAssignment = (next: InboxAssignmentFilter) => {
    setAssignment(next);
    // Narrowing the archive or the trash by assignee is a filter, not an exit from it: staying put
    // is what the operator asked for, and bouncing them back to live work would lose their place.
    if (taskFilter !== "archived" && taskFilter !== "trash") {
      if (taskFilter === "review" && next !== "review") setTaskFilter("all");
      if (taskFilter !== "review" && next === "review") setTaskFilter("review");
    }
    setOffset(0);
    setSectionOffsets(EMPTY_SECTION_OFFSETS);
  };
  const updateStatusFilter = (next: InboxStatusBucket | "") => {
    setStatusFilter(next);
    setOffset(0);
    setSectionOffsets(EMPTY_SECTION_OFFSETS);
  };
  // Either end moves on its own, and every move starts the period again from its first page —
  // offset 25 of the old period names nothing in the new one.
  const updateDateRange = (change: Partial<InboxDateRange>) => {
    setDateRange((current) => ({ ...current, ...change }));
    setPeriodPreset("custom");
    setOffset(0);
    setSectionOffsets(EMPTY_SECTION_OFFSETS);
  };
  const selectPeriod = (next: InboxPeriodPreset) => {
    setPeriodPreset(next);
    setDateRange((current) => inboxPeriodDateRange(next, new Date(), current));
    setOffset(0);
    setSectionOffsets(EMPTY_SECTION_OFFSETS);
  };
  const rememberFocusedRow = (
    row: ConversationListItem,
    index: number,
    section: InboxTaskFilter,
  ) => {
    publishContext({
      scrollTop: readPrimaryScrollTop(),
      focusedRow: { id: row.id, index, section },
    });
  };
  const captureFocusedRow = (
    event: React.FocusEvent<HTMLElement>,
    rows: ConversationListItem[],
    section: InboxTaskFilter,
  ) => {
    const target = event.target as HTMLElement;
    const focused = target.closest<HTMLElement>("tbody tr[tabindex='0']");
    if (!focused) return;
    const renderedRows = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>("tbody tr[tabindex='0']"),
    );
    const index = renderedRows.indexOf(focused);
    if (index >= 0 && rows[index]) rememberFocusedRow(rows[index], index, section);
  };
  const openConversation = (row: ConversationListItem, index: number, section: InboxTaskFilter) => {
    rememberFocusedRow(row, index, section);
    onOpen?.(String(row.target_request_no ?? row.target_request_id));
  };
  const conversationAriaLabel = (row: ConversationListItem) => `${t("inbox.openAria", {
    subject: row.subject || t("common.noSubject"),
    from: conversationCustomerLabel(row, t),
  })}${row.unopened ? `. ${t("inbox.unopened")}` : ""}`;

  const conversationColumns = (section: InboxTaskFilter, rows: ConversationListItem[]) => [
    { key: "selection", width: "44px", header: (
      <InboxSelectionCheckbox label={t("inbox.bulk.selectPage")}
        checked={rows.length > 0 && rows.every((row) => bulk.selectedIds.includes(row.target_request_id))}
        mixed={rows.some((row) => bulk.selectedIds.includes(row.target_request_id))}
        disabled={selectionDisabled || bulk.busy || !rows.some(hasBulkVersion)}
        onChange={(checked) => bulk.selectRows(rows, checked)} />
    ), cell: (row: ConversationListItem) => (
      <InboxSelectionCheckbox label={`${t("inbox.bulk.selectRequest")}: ${row.target_reference ?? row.target_request_no} · ${row.subject || t("common.noSubject")}`}
        checked={bulk.selectedIds.includes(row.target_request_id)} disabled={selectionDisabled || bulk.busy || !hasBulkVersion(row)}
        onChange={(checked) => bulk.selectRows([row], checked)} />
    ) },
    // #2106: who asks comes before what the mail is called, in the table and when stacked.
    { key: "customer", header: t("inbox.colCustomerEvent"), width: "30%", cell: (row: ConversationListItem) => {
      const eventParts = eventSummaryParts(row.event_summary);
      const eventText = eventParts.join(" · ");
      const requestMetadata = row.target_reference;
      const summaryText = [requestMetadata, eventText].filter(Boolean).join(" · ");
      const customerLabel = conversationCustomerLabel(row, t);
      // The accepted facts of this request, as the start-page queue showed them under its subject.
      const acceptedFacts = [
        row.customer_name && row.customer_name !== customerLabel ? row.customer_name : null,
        row.customer_type ? t(`factValue.${row.customer_type}`) : null,
        row.guest_count ? t("ws.guests", { n: guestCountText(row.guest_count, t) }) : null,
      ].filter((fact): fact is string => Boolean(fact)).join(" · ");
      return (
        <div className="triage-firma">
          <b title={customerLabel}>{customerLabel}</b>
          <FormCustomerSource row={row} />
          {acceptedFacts && (
            <span className="inbox-customer-facts" data-testid="inbox-customer-facts" title={acceptedFacts}>
              {acceptedFacts}
            </span>
          )}
          {summaryText && (
            <span className="inbox-customer-summary" title={summaryText}>
              {requestMetadata && <span className="inbox-request-summary">{requestMetadata}</span>}
              {requestMetadata && eventText && <span aria-hidden="true">{" · "}</span>}
              {eventText && (
                <span
                  className="inbox-event-summary"
                  data-testid="inbox-event-summary"
                  aria-label={`${t("inbox.eventSummaryAria")}: ${eventText}`}
                >
                  {eventText}
                </span>
              )}
            </span>
          )}
        </div>
      );
    } },
    { key: "subject", header: t("inbox.colSubject"), width: "34%", cell: (row: ConversationListItem, index: number) => (
      <div className={cx("inbox-subject-cell", row.highlighted && "inbox-newinfo")}>
        <div className="inbox-subject-copy">
          <span className="triage-subject-row">
            <a
              className="triage-subject work-item-link"
              title={row.subject || t("common.noSubject")}
              href={pathOf({ name: "mail", id: String(row.target_request_no ?? row.target_request_id) })}
              aria-label={conversationAriaLabel(row)}
              onClick={(event) => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                openConversation(row, index, section);
              }}
            >
              {row.subject || t("common.noSubject")}
            </a>
            {/* #2178: the pills stay on one line of their own, so their number never adds a line. */}
            {(row.unopened || row.incoming_work?.state === "processing"
              || (row.attention_reason && row.attention_reason !== "new_request")) && (
              <span className="inbox-subject-pills">
                {row.unopened && <StatusPill tone="info" variant="soft" size="sm" label={t("inbox.unopened")} />}
                {row.incoming_work?.state === "processing" && <StatusPill tone="progress" variant="soft" size="sm" label={t("incoming.processing")} />}
                {row.attention_reason && row.attention_reason !== "new_request" && (
                  <StatusPill
                    data-testid="attention-reason"
                    data-reason={row.attention_reason}
                    tone={ATTENTION_REASON_TONES[row.attention_reason]}
                    variant="soft"
                    size="sm"
                    label={t(`inbox.attention.${row.attention_reason}`)}
                  />
                )}
              </span>
            )}
          </span>
          <span className="inbox-conversation-snippet">{row.last_message_snippet || t("inbox.noMessagePreview")}</span>
        </div>
      </div>
    ) },
    { key: "assignee", header: t("inbox.colAssignee"), width: "19%", cell: (row: ConversationListItem) => {
      // #803: several people and whole teams can be on one request. The column names the first
      // two and counts the rest — the operator opens the request to see everybody.
      // Open work is assigned from the row; the archive and the trash only say who it was.
      if (canAssign && section !== "archived" && section !== "trash") {
        return (
          <RowAssignees
            key={`${scopeKey}:${row.target_request_id}`}
            row={row}
            currentUserId={user?.id}
            onChanged={() => { void load({ background: true }); }}
          />
        );
      }
      const { labels, extra } = assigneeSummary(row.assignees);
      if (!labels.length) {
        return <span className="subtle inbox-unassigned">{t("inbox.unassigned")}</span>;
      }
      return (
        <span className="inbox-assignee" title={row.assignees.map(assigneeName).join(", ")}>
          {labels.join(", ")}
          {extra > 0 ? <span className="subtle"> {t("inbox.assigneeMore", { n: extra })}</span> : null}
        </span>
      );
    } },
    { key: "activity", header: t(section === "trash" ? "inbox.colPurgeAt" : "inbox.colLatestActivity"), width: "17%", cell: (row: ConversationListItem) => (
      <span className="inbox-activity-cell">
        {/* #1787: in the trash the date that matters is the one it is deleted for good. */}
        {section === "trash" && row.purge_at
          ? <span data-testid="inbox-purge-at" title={t("inbox.trashPurgeAt", { date: fmtDate(row.purge_at) })}>{fmtDate(row.purge_at)}</span>
          : <AgeCell value={row.latest_activity_at} />}
        <Icon name="chevronRight" size={15} className="inbox-row-chevron" aria-hidden="true" />
      </span>
    ) },
  ];

  const toggleSection = (section: InboxActiveSection) => {
    setCollapsedSections((current) => ({ ...current, [section]: !current[section] }));
  };

  const renderPagination = (
    section: InboxTaskFilter,
    page: ConversationListPage | null,
    currentOffset: number,
  ) => {
    if (!page) return null;
    const pageNumber = Math.floor(currentOffset / page.limit) + 1;
    const pageCount = Math.max(1, Math.ceil(page.total / page.limit));
    const update = (nextOffset: number) => setOffset(nextOffset);
    return (
      <div
        className="inbox-pagination"
        aria-label={`${t("inbox.paginationAria")}: ${t(`inbox.task.${section}`)}`}
        data-testid={`inbox-pagination-${section}`}
      >
        <Button
          variant="secondary"
          size="sm"
          disabled={currentOffset === 0}
          onClick={() => update(Math.max(0, currentOffset - page.limit))}
        >
          {t("inbox.previousPage")}
        </Button>
        <span>{t("inbox.pageOf", { page: pageNumber, total: pageCount })}</span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page.next_offset === null}
          onClick={() => update(page.next_offset ?? currentOffset)}
        >
          {t("inbox.nextPage")}
        </Button>
      </div>
    );
  };

  const renderSection = (
    section: InboxTaskFilter,
    page: ConversationListPage | null,
    currentOffset: number,
  ) => {
    const rows = page?.items ?? [];
    const archived = section === "archived";
    const trash = section === "trash";
    const emptyTitleKey = archived ? "inbox.emptyArchiveTitle" : trash ? "inbox.emptyTrashTitle" : "inbox.emptyTitle";
    const emptyMessageKey = archived ? "inbox.emptyArchiveMessage" : trash ? "inbox.emptyTrashMessage" : "inbox.emptyMessage";
    const compactEmpty = !loading && !error && page?.total === 0;
    const headingId = `inbox-section-${section}-title`;
    const contentId = `inbox-section-${section}-content`;
    const grouped = groupedView;
    const activeSection = grouped ? section as InboxActiveSection : null;
    const collapsed = activeSection ? collapsedSections[activeSection] : false;
    const remainingPreviewCount = groupedPreviewRemainingCount(page);
    return (
      <section
        key={section}
        className={cx(
          "inbox-work-section",
          grouped && `inbox-work-section--${section}`,
          (archived || trash) && "inbox-work-section--archive",
          !grouped && "inbox-work-section--single",
          collapsed && "is-collapsed",
        )}
        aria-labelledby={grouped ? headingId : undefined}
        aria-label={grouped ? undefined : t(`inbox.task.${section}`)}
        data-testid={`inbox-section-${section}`}
      >
        {grouped && (
          <header className="inbox-work-section__head">
            <h2 id={headingId}>
              <button
                type="button"
                className="inbox-work-section__toggle"
                aria-expanded={!collapsed}
                aria-controls={contentId}
                data-testid={`inbox-section-${section}-toggle`}
                onClick={() => { if (activeSection) toggleSection(activeSection); }}
              >
                <Icon
                  name={collapsed ? "chevronDown" : "chevronUp"}
                  size={14}
                  className="inbox-work-section__chevron"
                />
                <span className={`inbox-work-section__dot inbox-work-section__dot--${section}`} aria-hidden="true" />
                <span className="inbox-work-section__label">{t(`inbox.task.${section}`)}</span>
                <span className="inbox-work-section__count">
                  {page?.total ?? taskCounts[section]}
                </span>
              </button>
            </h2>
          </header>
        )}
        <div
          id={contentId}
          data-testid={`inbox-section-${section}-content`}
          hidden={collapsed}
        >
          <div className="inbox-conversation-panel">
            {/* #2248: the trash has no hint; each row already shows the date it is deleted for good. */}
            {archived && (
              <p className="subtle inbox-archive-hint" data-testid="inbox-archive-hint">
                {t("inbox.archiveHint")}
              </p>
            )}
            {error && page && <Banner tone="danger" title={t("inbox.loadErrorTitle")}
              actions={<Button variant="secondary" size="sm" onClick={() => { void load(); }}>{t("common.retry")}</Button>}>
              {error}
            </Banner>}
            {compactEmpty ? (
              <div className="inbox-work-section__empty" role="status">
                {t(emptyTitleKey)}
              </div>
            ) : (
              <DataTable
                joined
                columns={conversationColumns(section, rows)}
                rows={rows}
                rowKey={(row: ConversationListItem) => row.target_request_id}
                // #2105: the rows of an open-work list carry the colour of its category. The list
                // is the server's worklist query, so the bar says what the overview card counts.
                className={cx("inbox-table conversation-table inbox-table--selectable",
                  (INBOX_ACTIVE_SECTIONS as readonly string[]).includes(section) && `inbox-table--${section}`)}
                loading={loading && page === null}
                // #1543: as many placeholder rows as a grouped preview can show.
                skeletonRows={grouped ? GROUPED_PREVIEW_PAGE_SIZE : undefined}
                error={page === null ? error : null}
                onRetry={() => { void load(); }}
                errorTitle={t("inbox.loadErrorTitle")}
                retryLabel={t("common.retry")}
                onFocusCapture={(event: React.FocusEvent<HTMLElement>) => captureFocusedRow(event, rows, section)}
                onPointerDownCapture={(event: React.PointerEvent<HTMLElement>) => {
                  const target = event.target as HTMLElement;
                  pressInAssigneeRef.current = Boolean(target.closest(".row-assignees"));
                  // #2302: a press that opens the request starts its read; the workspace takes it over.
                  if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
                    && !pressInAssigneeRef.current) prefetchPressedRequest(target);
                }}
                onKeyDownCapture={() => { pressInAssigneeRef.current = false; }}
                // Runs after the row has handled the click, so a later click without a press starts clean.
                onClick={() => { pressInAssigneeRef.current = false; }}
                onRowClick={(row: ConversationListItem, index: number) => {
                  if (!pressInAssigneeRef.current) openConversation(row, index, section);
                }}
                rowAriaLabel={conversationAriaLabel}
                emptyTitle={t(emptyTitleKey)}
                emptyMessage={t(emptyMessageKey)}
              />
            )}
          </div>
          {grouped && activeSection && remainingPreviewCount > 0 ? (
            <button
              type="button"
              className="inbox-work-section__more"
              data-testid={`inbox-section-${section}-more`}
              onClick={() => openTaskListFromPreview(activeSection)}
            >
              <span>{t(`inbox.previewMore.${activeSection}`, { n: remainingPreviewCount })}</span>
              <Icon name="chevronRight" size={14} aria-hidden="true" />
            </button>
          ) : !grouped && renderPagination(section, page, currentOffset)}
        </div>
      </section>
    );
  };

  // #2249: the archive and the trash are lists to look something up in, not the place to steer
  // today's work, so they drop the work cards and the side rail and say which list is open.
  const storedList = taskFilter === "archived" || taskFilter === "trash";

  return (
    <div className="page inbox-redesign">
      {storedList && (
        <div className="ov-back">
          <button type="button" className="neo-btn neo-btn--ghost neo-btn--sm"
            data-testid="overview-back" onClick={() => selectTask(DEFAULT_TASK)}>
            <Icon name="arrowLeft" size={16} />
            {t("ws.backToInbox")}
          </button>
        </div>
      )}
      <div className="ov-head">
        <div className="ov-heading">
          <h1 className="ov-title">{t(storedList ? `inbox.task.${taskFilter}` : "overview.workTitle")}</h1>
        </div>
        <OverviewHeadTools
          incoming={incomingRead.scope === scopeKey ? incomingRead.read : { status: "loading" }}
          filter={{
            expanded: filtersExpanded,
            controls: "inbox-filter-panel",
            testId: "overview-filter-toggle",
            onToggle: () => setFiltersExpanded((current) => !current),
          }}
          current={taskFilter}
          onSelectList={selectTask}
          filed={filed}
        />
      </div>

      {/* An unclear delivery is shown above the page's content; its calm state joins the footer. */}
      <DeliveryReconciliationQueue
        onOpenRequest={onOpen}
        splitInboxPlacement
        inboxFooterHost={footerHost}
        inboxFooterAriaLabel={t("inbox.footer.aria")}
        inboxFooterFacts={(
          <InboxFooterFacts
            config={config}
            terminalAnalysisFailures={terminalAnalysisFailures}
          />
        )}
        inboxFooterAction={onOpenSystemStatus && (
          <button type="button" className="inbox-system-footer__action" onClick={onOpenSystemStatus}>
            {t("overview.footer.openSystemStatus")}
            <Icon name="chevronRight" size={14} />
          </button>
        )}
      />

      <div className={cx("ov-main", storedList && "ov-main--single")}>
        <div className="ov-workspace">
          {!storedList && (
            <div className="ov-stats" role="group" aria-label={t("inbox.taskFiltersAria")}>
              {INBOX_ACTIVE_SECTIONS.map((task) => (
                <button
                  ref={(element) => { statRefs.current[task] = element ?? undefined; }}
                  type="button"
                  key={task}
                  className={cx("ov-stat", `ov-stat--${task}`, taskFilter === task && "is-active")}
                  aria-pressed={taskFilter === task}
                  data-testid={`overview-stat-${task}`}
                  onClick={() => selectTask(task)}
                >
                  <span className="ov-stat__icon"><Icon name={STAT_ICONS[task]} size={20} /></span>
                  <span className="ov-stat__copy">
                    <span className="ov-stat__value">{countsKnown ? taskCounts[task] : "—"}</span>
                    <span className="ov-stat__label">{t(`inbox.task.${task}`)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="inbox-list">
            {/* #2103: the row that named the list is gone; the cards, the head buttons and the
                section heads say which list is open. The name stays for assistive technology. */}
            <h2 id="inbox-list-title" className="sr-only">
              {t(`inbox.task.${taskFilter}`)}{" "}
              {!countsKnown
                ? ""
                : groupedView && INBOX_ACTIVE_SECTIONS.every((section) => sectionPages[section])
                  ? groupedTotalCount
                  : conversationPage?.total ?? taskCounts[taskFilter]}
            </h2>

            <div
              id="inbox-filter-panel"
              className="neo-filterbar inbox-toolbar"
              aria-label={t("inbox.filterAria")}
              hidden={!filtersExpanded}
            >
              <div className="neo-filterbar__search">
                <Icon name="search" size={16} />
                <input
                  type="search"
                  value={q}
                  placeholder={t("inbox.searchPlaceholderWide")}
                  aria-label={t("inbox.searchAria")}
                  onChange={(event) => {
                    setQ(event.target.value);
                    setOffset(0);
                    setSectionOffsets(EMPTY_SECTION_OFFSETS);
                  }}
                />
              </div>
              <div className="filter-select inbox-filter-status">
                <Select
                  size="sm"
                  value={statusFilter}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    updateStatusFilter(event.target.value as InboxStatusBucket | "");
                  }}
                  options={[
                    { value: "", label: t("inbox.statusAll") },
                    ...INBOX_STATUS_BUCKETS.map((status) => ({
                      value: status,
                      label: t(`status.lifecycle.${status}`),
                    })),
                  ]}
                  aria-label={t("inbox.filterStatusAria")}
                />
              </div>
              <InboxPeriodControl
                preset={periodPreset}
                range={dateRange}
                problem={dateRangeProblem}
                onPreset={selectPeriod}
                onRange={updateDateRange}
              />
              <div className="filter-select inbox-filter-assignee">
                <Select
                  size="sm"
                  value={assignment}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => updateAssignment(event.target.value as InboxAssignmentFilter)}
                  options={[
                    { value: "all", label: t("inbox.assigneeAll") },
                    { value: "review", label: t("inbox.task.review") },
                    { value: "unassigned", label: t("inbox.unassigned") },
                  ]}
                  aria-label={t("inbox.filterAssigneeAria")}
                />
              </div>
              <div className="neo-filterbar__spacer" />
              <div className="filter-select inbox-filter-sort">
                <Select
                  size="sm"
                  value={sort}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    setSort(event.target.value as ConversationSort);
                    setOffset(0);
                    setSectionOffsets(EMPTY_SECTION_OFFSETS);
                  }}
                  options={INBOX_SORTS.map((name) => ({ value: name, label: t(`inbox.sort.${name}`) }))}
                  aria-label={t("inbox.filterSortAria")}
                />
              </div>
            </div>

            {dateRangeProblem && (
              <p className="inbox-filter-problem" role="alert" data-testid="inbox-date-range-problem">
                {t(`inbox.dateRange.${dateRangeProblem}`)}
              </p>
            )}

            <div
              id="inbox-task-panel"
              role="region"
              aria-labelledby="inbox-list-title"
              data-testid="conversation-list"
              className="inbox-worklist"
            >
              {groupedView
                ? INBOX_ACTIVE_SECTIONS.map((section) => renderSection(
                    section,
                    sectionPages[section],
                    sectionOffsets[section],
                  ))
                : renderSection(taskFilter, conversationPage, offset)}
            </div>
            {bulk.toolbar}
          </div>
        </div>

        {!storedList && (
          <div className="ov-rail">
            <OverviewMetricsPanel />
            <PresencePanel onOpenStaff={onOpenStaff} />
          </div>
        )}
      </div>
      <div ref={setFooterHost} className="inbox-system-footer-host" />
    </div>
  );
}
